import React, { useState } from 'react';
import { useSiteSettings, SiteSettings } from '../context/SiteSettingsContext';
import './AdminSettings.css';

const FIELDS: { key: keyof SiteSettings; label: string }[] = [
  { key: 'creditCardLimit', label: 'Credit Card Limit' },
  { key: 'shippingNJ', label: 'Shipping — Canada to NJ' },
  { key: 'shippingCA', label: 'Shipping — Canada to CA' },
  { key: 'shippingTX', label: 'Shipping — Canada to TX' },
  { key: 'customsBrokerFee', label: 'Customs Broker Fee' },
  { key: 'duty', label: 'Duty' },
  { key: 'storageOutdoor', label: 'Storage — Outdoor Covered (per month)' },
  { key: 'storageIndoorShared', label: 'Storage — Indoor Shared (per month)' },
  { key: 'storageIndoorExclusive', label: 'Storage — Indoor Exclusive (per month)' },
  { key: 'taxCanadianResidents', label: 'Tax — Canadian Residents (HST)' },
  { key: 'taxNonResidentsLocalPickup', label: 'Tax — Non Residents, Local Pickup (HST)' },
  { key: 'taxNonResidentsShipped', label: 'Tax — Non Residents, Shipped Outside Canada' },
];

const AdminSettings: React.FC = () => {
  const { settings, updateSettings } = useSiteSettings();
  const [draft, setDraft] = useState<SiteSettings>(settings);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleChange = (key: keyof SiteSettings, value: string) => {
    setDraft(prev => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateSettings(draft);
      setSaved(true);
    } catch (err) {
      console.error('Settings save failed:', err);
      alert('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-settings">
      <div className="admin-settings-inner">
        <header className="admin-settings-header">
          <h2>Settings</h2>
          <p className="admin-settings-lead">
            Manage prices and rates shown on the public Shipping and Payment pages.
          </p>
        </header>

        <section className="admin-settings-card" aria-labelledby="settings-prices">
          <h3 id="settings-prices">Prices &amp; Rates</h3>
          <form onSubmit={handleSave} className="admin-settings-form">
            {FIELDS.map(({ key, label }) => (
              <div className="admin-settings-field" key={key}>
                <label htmlFor={`field-${key}`}>{label}</label>
                <input
                  id={`field-${key}`}
                  type="text"
                  value={draft[key]}
                  onChange={e => handleChange(key, e.target.value)}
                />
              </div>
            ))}
            <div className="admin-settings-actions">
              <button type="submit" className="admin-settings-save" disabled={saving}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
              {saved && <span className="admin-settings-saved">Saved</span>}
            </div>
          </form>
        </section>

        <section className="admin-settings-card" aria-labelledby="settings-session">
          <h3 id="settings-session">Session</h3>
          <ul className="admin-settings-list">
            <li>Authentication is stored in session storage and clears when the tab closes.</li>
            <li>Credentials are defined in the app code — not suitable for public deployment.</li>
          </ul>
        </section>

        <section className="admin-settings-card" aria-labelledby="settings-inventory">
          <h3 id="settings-inventory">Inventory</h3>
          <p>
            Vehicle data is managed from the <strong>Inventory</strong> screen and persists in the browser
            via the cars context.
          </p>
        </section>
      </div>
    </div>
  );
};

export default AdminSettings;
